import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Bell, Plus, Trash2, Edit2, CheckCircle2, Clock, AlertTriangle,
  Calendar, Euro, Loader2, RefreshCw, SplitSquareHorizontal, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminPaymentReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPartialDialog, setShowPartialDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [partialTarget, setPartialTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [partialForm, setPartialForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], note: '' });
  const [formData, setFormData] = useState({
    title: '', description: '', amount: '', due_date: '',
    reminder_days_before: '3', repeat_interval: 'none', category: 'sonstiges'
  });

  const loadReminders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/payment-reminders');
      setReminders(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Erinnerungen');
    }
    setLoading(false);
  };

  useEffect(() => { loadReminders(); }, []);

  const resetForm = () => {
    setFormData({ title: '', description: '', amount: '', due_date: '', reminder_days_before: '3', repeat_interval: 'none', category: 'sonstiges' });
    setEditingReminder(null);
  };

  const openCreateDialog = () => { resetForm(); setShowDialog(true); };

  const openEditDialog = (reminder) => {
    setEditingReminder(reminder);
    setFormData({
      title: reminder.title || '', description: reminder.description || '',
      amount: reminder.amount?.toString() || '', due_date: reminder.due_date?.split('T')[0] || '',
      reminder_days_before: reminder.reminder_days_before?.toString() || '3',
      repeat_interval: reminder.repeat_interval || 'none', category: reminder.category || 'sonstiges'
    });
    setShowDialog(true);
  };

  const openPartialDialog = (reminder) => {
    setPartialTarget(reminder);
    const remaining = (reminder.amount || 0) - (reminder.paid_total || 0);
    setPartialForm({ amount: remaining > 0 ? remaining.toFixed(2) : '', date: new Date().toISOString().split('T')[0], note: '' });
    setShowPartialDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.amount || !formData.due_date) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    try {
      const data = { ...formData, amount: parseFloat(formData.amount), reminder_days_before: parseInt(formData.reminder_days_before) };
      if (editingReminder) {
        await api.put(`/admin/payment-reminders/${editingReminder.id}`, data);
        toast.success('Erinnerung aktualisiert');
      } else {
        await api.post('/admin/payment-reminders', data);
        toast.success('Erinnerung erstellt');
      }
      setShowDialog(false);
      resetForm();
      loadReminders();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Diese Erinnerung wirklich löschen?')) return;
    try {
      await api.delete(`/admin/payment-reminders/${id}`);
      toast.success('Erinnerung gelöscht');
      loadReminders();
    } catch (error) { toast.error('Fehler beim Löschen'); }
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.post(`/admin/payment-reminders/${id}/mark-paid`);
      toast.success('Als bezahlt markiert');
      loadReminders();
    } catch (error) { toast.error('Fehler beim Aktualisieren'); }
  };

  const handlePartialPayment = async () => {
    if (!partialForm.amount || parseFloat(partialForm.amount) <= 0) {
      toast.error('Bitte einen gültigen Betrag eingeben');
      return;
    }
    try {
      await api.post(`/admin/payment-reminders/${partialTarget.id}/partial-payment`, {
        amount: parseFloat(partialForm.amount), date: partialForm.date, note: partialForm.note
      });
      toast.success('Teilzahlung erfasst');
      setShowPartialDialog(false);
      loadReminders();
    } catch (error) { toast.error('Fehler beim Erfassen der Teilzahlung'); }
  };

  const handleDeletePartial = async (reminderId, partialId) => {
    try {
      await api.delete(`/admin/payment-reminders/${reminderId}/partial-payment/${partialId}`);
      toast.success('Teilzahlung gelöscht');
      loadReminders();
    } catch (error) { toast.error('Fehler beim Löschen'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';
  const formatCurrency = (a) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(a || 0);

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(dueDate); due.setHours(0,0,0,0);
    return Math.ceil((due - today) / (1000*60*60*24));
  };

  const getStatusBadge = (reminder) => {
    if (reminder.status === 'paid') return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3" /> Bezahlt</span>;
    if (reminder.status === 'partial') return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><SplitSquareHorizontal className="w-3 h-3" /> Teilbezahlt</span>;
    const daysUntil = getDaysUntilDue(reminder.due_date);
    if (daysUntil < 0) return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" /> Überfällig ({Math.abs(daysUntil)} Tage)</span>;
    if (daysUntil <= 3) return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3" /> In {daysUntil} Tagen</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><Clock className="w-3 h-3" /> In {daysUntil} Tagen</span>;
  };

  const pendingReminders = reminders.filter(r => r.status !== 'paid');
  const paidReminders = reminders.filter(r => r.status === 'paid');
  const totalPending = pendingReminders.reduce((sum, r) => sum + ((r.amount || 0) - (r.paid_total || 0)), 0);

  const CATEGORIES = { 'miete': 'Miete', 'versicherung': 'Versicherung', 'steuern': 'Steuern', 'lieferanten': 'Lieferanten', 'personal': 'Personal', 'sonstiges': 'Sonstiges' };

  const renderReminderItem = (reminder, isPaid = false) => {
    const partials = reminder.partial_payments || [];
    const paidTotal = reminder.paid_total || 0;
    const remaining = (reminder.amount || 0) - paidTotal;
    const isExpanded = expandedId === reminder.id;

    return (
      <div key={reminder.id} className={`rounded-lg border ${isPaid ? 'bg-green-50/50 border-green-200 opacity-75' : getDaysUntilDue(reminder.due_date) < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`reminder-item-${reminder.id}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4">
          <div className="flex-1 mb-3 sm:mb-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium text-gray-900">{reminder.title}</h3>
              <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">{CATEGORIES[reminder.category] || reminder.category}</span>
              {getStatusBadge(reminder)}
            </div>
            {reminder.description && <p className="text-sm text-gray-500 mb-1">{reminder.description}</p>}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="flex items-center gap-1 text-gray-600"><Calendar className="w-4 h-4" /> Fällig: {formatDate(reminder.due_date)}</span>
              {paidTotal > 0 && !isPaid && (
                <span className="text-orange-600 font-medium">Bezahlt: {formatCurrency(paidTotal)} / Rest: {formatCurrency(remaining)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-xl font-bold text-[#9B1B30]">{formatCurrency(reminder.amount)}</span>
              {paidTotal > 0 && !isPaid && <p className="text-xs text-orange-600">Rest: {formatCurrency(remaining)}</p>}
            </div>
            {!isPaid && (
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => openPartialDialog(reminder)} title="Teilzahlung" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50" data-testid={`partial-payment-${reminder.id}`}>
                  <SplitSquareHorizontal className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(reminder.id)} title="Voll bezahlt" className="text-green-600 hover:text-green-700 hover:bg-green-50">
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => openEditDialog(reminder)}><Edit2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(reminder.id)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
              </div>
            )}
            {partials.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setExpandedId(isExpanded ? null : reminder.id)}>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            )}
          </div>
        </div>
        {isExpanded && partials.length > 0 && (
          <div className="border-t bg-white/50 px-4 py-3">
            <p className="text-xs font-semibold text-gray-500 mb-2">Teilzahlungen:</p>
            <div className="space-y-1.5">
              {partials.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm bg-orange-50 rounded px-3 py-1.5">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-orange-700">{formatCurrency(p.amount)}</span>
                    <span className="text-gray-500">{formatDate(p.date)}</span>
                    {p.note && <span className="text-gray-400">- {p.note}</span>}
                  </div>
                  {!isPaid && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeletePartial(reminder.id, p.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#9B1B30] to-[#c42847] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <Bell className="w-8 h-8" /> Zahlungserinnerungen
              </h1>
              <p className="text-white/80 mt-1">Eigene Zahlungen verwalten und Erinnerungen erhalten</p>
            </div>
            <Button onClick={openCreateDialog} className="bg-white text-[#9B1B30] hover:bg-gray-100" data-testid="new-reminder-btn">
              <Plus className="w-4 h-4 mr-2" /> Neue Zahlung
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center"><Clock className="w-6 h-6 text-yellow-600" /></div><div><p className="text-sm text-gray-500">Ausstehend</p><p className="text-2xl font-bold text-gray-900">{pendingReminders.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center"><Euro className="w-6 h-6 text-red-600" /></div><div><p className="text-sm text-gray-500">Offener Betrag</p><p className="text-2xl font-bold text-[#9B1B30]">{formatCurrency(totalPending)}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-500">Bezahlt</p><p className="text-2xl font-bold text-green-600">{paidReminders.length}</p></div></CardContent></Card>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Offene Zahlungen</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadReminders}><RefreshCw className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-[#9B1B30]" /></div>
            ) : pendingReminders.length === 0 ? (
              <div className="text-center py-8 text-gray-500"><Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>Keine ausstehenden Zahlungen</p></div>
            ) : (
              <div className="space-y-3">{pendingReminders.map((r) => renderReminderItem(r))}</div>
            )}
          </CardContent>
        </Card>

        {paidReminders.length > 0 && (
          <Card className="mt-6">
            <CardHeader><CardTitle className="text-gray-600">Bezahlt</CardTitle></CardHeader>
            <CardContent><div className="space-y-2">{paidReminders.slice(0, 10).map((r) => renderReminderItem(r, true))}</div></CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingReminder ? 'Zahlung bearbeiten' : 'Neue Zahlung anlegen'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Titel *</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="z.B. Miete Januar" className="mt-1" data-testid="input-title" /></div>
            <div><Label>Beschreibung</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Weitere Details..." className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Betrag *</Label><Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" className="mt-1" data-testid="input-amount" /></div>
              <div><Label>Kategorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORIES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Fälligkeitsdatum *</Label><Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="mt-1" data-testid="input-due-date" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Erinnerung</Label>
                <Select value={formData.reminder_days_before} onValueChange={(v) => setFormData({ ...formData, reminder_days_before: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1">1 Tag</SelectItem><SelectItem value="3">3 Tage</SelectItem><SelectItem value="7">7 Tage</SelectItem><SelectItem value="14">14 Tage</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Wiederholung</Label>
                <Select value={formData.repeat_interval} onValueChange={(v) => setFormData({ ...formData, repeat_interval: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Einmalig</SelectItem><SelectItem value="monthly">Monatlich</SelectItem><SelectItem value="quarterly">Vierteljährlich</SelectItem><SelectItem value="yearly">Jährlich</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} className="bg-[#9B1B30] hover:bg-[#7d1526] text-white" data-testid="submit-reminder-btn">{editingReminder ? 'Speichern' : 'Anlegen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Dialog */}
      <Dialog open={showPartialDialog} onOpenChange={setShowPartialDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Teilzahlung erfassen</DialogTitle></DialogHeader>
          {partialTarget && (
            <div className="space-y-4 py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-900">{partialTarget.title}</p>
                <p className="text-gray-600">Gesamtbetrag: {formatCurrency(partialTarget.amount)}</p>
                {(partialTarget.paid_total || 0) > 0 && <p className="text-orange-600">Bereits bezahlt: {formatCurrency(partialTarget.paid_total)}</p>}
                <p className="font-semibold text-[#9B1B30]">Restbetrag: {formatCurrency((partialTarget.amount || 0) - (partialTarget.paid_total || 0))}</p>
              </div>
              <div><Label>Betrag der Teilzahlung *</Label><Input type="number" step="0.01" value={partialForm.amount} onChange={(e) => setPartialForm({ ...partialForm, amount: e.target.value })} className="mt-1" data-testid="partial-amount" /></div>
              <div><Label>Datum *</Label><Input type="date" value={partialForm.date} onChange={(e) => setPartialForm({ ...partialForm, date: e.target.value })} className="mt-1" data-testid="partial-date" /></div>
              <div><Label>Notiz (wo/wie bezahlt)</Label><Input value={partialForm.note} onChange={(e) => setPartialForm({ ...partialForm, note: e.target.value })} placeholder="z.B. Bar bezahlt, Überweisung..." className="mt-1" data-testid="partial-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartialDialog(false)}>Abbrechen</Button>
            <Button onClick={handlePartialPayment} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-partial-btn">Teilzahlung erfassen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentReminders;
